import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  ArrowLeft, Crown, Check, Zap, Shield, Brain, Sparkles,
  TrendingUp, Clock, AlertTriangle, Loader2, CreditCard,
  ChevronRight, Star, Lock, Unlock, RefreshCw, XCircle
} from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "../App";

// ============ PREMIUM FEATURES LIST ============
const PREMIUM_FEATURES = [
  {
    icon: Brain,
    title: "4-AI Consensus Engine",
    description: "Claude, GPT-4, Grok, and Gemini analyze every strategy together",
    free: false,
  },
  {
    icon: Zap,
    title: "Internal Swap Engine (ISE)",
    description: "AI-matched internal swaps at 0.15% — cheaper than any DEX",
    free: false,
  },
  {
    icon: Shield,
    title: "SAS Priority Access",
    description: "Priority treasury access for instant card settlements",
    free: false,
  },
  {
    icon: TrendingUp,
    title: "Advanced Yield Strategies",
    description: "Multi-chain yield optimization across 12 chains, 33 protocols",
    free: false,
  },
  {
    icon: CreditCard,
    title: "Reduced Conversion Fees",
    description: "Lower fees on every card purchase using fallback tokens",
    free: false,
  },
  {
    icon: Sparkles,
    title: "Tax-Loss Harvesting",
    description: "Xavier automatically identifies and executes tax-saving swaps",
    free: false,
  },
  {
    icon: Star,
    title: "Cross-Chain Matching",
    description: "ISE matches swaps across different blockchains via treasury bridge",
    free: false,
  },
  {
    icon: RefreshCw,
    title: "Smart Spending Rules",
    description: "AI-optimized fallback logic — never sell the wrong token",
    free: false,
  },
];

const FREE_FEATURES = [
  "Basic portfolio tracking",
  "Price alerts and news",
  "Market sentiment analysis",
  "Single-chain yield suggestions",
];

// ============ SUBSCRIPTION STATUS BADGE ============
const StatusBadge = ({ subscription }) => {
  if (!subscription) return null;

  if (subscription.active && !subscription.in_grace_period) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm font-medium">
        <Check className="w-3.5 h-3.5" />
        Active Premium
      </span>
    );
  }

  if (subscription.in_grace_period) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-sm font-medium animate-pulse">
        <AlertTriangle className="w-3.5 h-3.5" />
        Payment Due — Grace Period
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-white/50 text-sm font-medium">
      <Lock className="w-3.5 h-3.5" />
      Free Tier
    </span>
  );
};

// ============ MAIN COMPONENT ============
const Subscription = () => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [price, setPrice] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Fetch subscription status
  useEffect(() => {
    fetchSubscription();
    fetchPrice();
  }, []);

  const fetchSubscription = async () => {
    try {
      // Get wallet address from localStorage or auth context
      const walletAddress = localStorage.getItem("wallet_address") || "";
      if (!walletAddress) {
        setLoading(false);
        return;
      }

      const { data } = await axios.get(`${API}/subscription/details/${walletAddress}`);
      setSubscription(data);
    } catch (err) {
      console.error("Failed to fetch subscription:", err);
      // Default to free tier display
      setSubscription({ active: false, tier: "free" });
    } finally {
      setLoading(false);
    }
  };

  const fetchPrice = async () => {
    try {
      const { data } = await axios.get(`${API}/subscription/price`);
      setPrice(data);
    } catch (err) {
      setPrice({ price_usd: 20, billing_period_days: 30, recommended_approval_usd: 240 });
    }
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      // In production, this triggers the wallet's USDC approval + subscribe() call
      // For now, show the flow
      toast.info("Connect your wallet to subscribe with USDC", {
        description: "You'll approve USDC spending, then the first month is charged immediately.",
      });

      // TODO: Integrate with wallet provider (ethers.js / wagmi)
      // Step 1: usdc.approve(subscriptionContract, recommendedApproval)
      // Step 2: subscriptionContract.subscribe()
      // Step 3: Refresh subscription status

      // Simulate for demo
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success("Premium activated!", {
        description: "Welcome to Xavier Premium. All features are now unlocked.",
      });
      fetchSubscription();
    } catch (err) {
      toast.error("Subscription failed", {
        description: err.message || "Please try again.",
      });
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      // In production: subscriptionContract.cancel()
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success("Subscription cancelled", {
        description: "Your premium access continues until the end of this billing period.",
      });
      setShowCancelConfirm(false);
      fetchSubscription();
    } catch (err) {
      toast.error("Cancellation failed", { description: err.message });
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] to-[#1A1A2E] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#FFD700] animate-spin" />
      </div>
    );
  }

  const isPremium = subscription?.active && !subscription?.in_grace_period;
  const isGrace = subscription?.in_grace_period;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] to-[#1A1A2E] text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0A0E27]/80 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-white/60 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-[#FFD700]" />
              <h1 className="text-lg font-bold">Premium</h1>
            </div>
          </div>
          <StatusBadge subscription={subscription} />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-4"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FDB931] mx-auto">
            <Crown className="w-10 h-10 text-[#002366]" />
          </div>
          <h2 className="text-3xl font-bold">
            {isPremium ? "You're Premium" : "Upgrade to Premium"}
          </h2>
          <p className="text-white/50 max-w-md mx-auto">
            {isPremium
              ? "You have full access to Xavier's most powerful features."
              : "Unlock the full power of Xavier's 4-AI consensus engine, internal swap matching, and priority card settlement."}
          </p>
        </motion.div>

        {/* Active Subscription Details */}
        <AnimatePresence>
          {isPremium && subscription && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <Card className="glass-card border-[#FFD700]/20">
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                    <div>
                      <p className="text-white/40 text-xs mb-1">Status</p>
                      <p className="text-green-400 font-semibold flex items-center gap-1">
                        <Check className="w-4 h-4" /> Active
                      </p>
                    </div>
                    <div>
                      <p className="text-white/40 text-xs mb-1">Tier</p>
                      <p className="text-[#FFD700] font-semibold capitalize">{subscription.tier}</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-xs mb-1">Streak</p>
                      <p className="text-white font-semibold">
                        {subscription.consecutive_months} month{subscription.consecutive_months !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/40 text-xs mb-1">Renews In</p>
                      <p className="text-white font-semibold flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5 text-white/40" />
                        {subscription.days_until_expiry} days
                      </p>
                    </div>
                  </div>

                  {subscription.total_paid_usd > 0 && (
                    <div className="mt-4 pt-4 border-t border-white/10 flex justify-between items-center">
                      <span className="text-white/40 text-sm">Lifetime paid</span>
                      <span className="text-white font-medium">${subscription.total_paid_usd.toFixed(2)} USDC</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Grace Period Warning */}
        <AnimatePresence>
          {isGrace && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
            >
              <Card className="border-yellow-500/30 bg-yellow-500/10">
                <CardContent className="p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-yellow-400 font-semibold text-sm">Payment overdue</p>
                    <p className="text-white/60 text-sm mt-1">
                      Your subscription payment couldn't be collected. Please ensure you have
                      at least ${price?.price_usd || 20} USDC and sufficient approval in your wallet.
                      Premium access will be revoked after the grace period ends.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Pricing Card (show when not premium) */}
        {!isPremium && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="glass-card border-[#FFD700]/30 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/5 to-transparent pointer-events-none" />
              <CardContent className="p-8 relative">
                <div className="text-center space-y-4">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold text-[#FFD700]">
                      ${price?.price_usd || 20}
                    </span>
                    <span className="text-white/40 text-lg">/month</span>
                  </div>
                  <p className="text-white/50 text-sm">
                    Paid in USDC • Cancel anytime • 3-day grace period
                  </p>

                  <Button
                    onClick={handleSubscribe}
                    disabled={subscribing}
                    className="w-full max-w-sm mx-auto btn-gold text-lg py-6 rounded-xl font-semibold"
                  >
                    {subscribing ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Unlock className="w-5 h-5 mr-2" />
                    )}
                    {subscribing ? "Connecting Wallet..." : "Subscribe with USDC"}
                  </Button>

                  <p className="text-white/30 text-xs">
                    Your wallet will be asked to approve {price?.recommended_approval_usd || 240} USDC
                    (12 months). Only ${price?.price_usd || 20} is charged now.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Premium Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Crown className="w-5 h-5 text-[#FFD700]" />
            Premium Features
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {PREMIUM_FEATURES.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * i }}
              >
                <Card className={`glass-card h-full transition-all duration-300 ${
                  isPremium
                    ? "border-[#FFD700]/10 hover:border-[#FFD700]/30"
                    : "border-white/5 hover:border-white/15"
                }`}>
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isPremium
                        ? "bg-[#FFD700]/20"
                        : "bg-white/5"
                    }`}>
                      <feature.icon className={`w-5 h-5 ${
                        isPremium ? "text-[#FFD700]" : "text-white/30"
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`font-medium text-sm ${
                          isPremium ? "text-white" : "text-white/60"
                        }`}>
                          {feature.title}
                        </p>
                        {isPremium ? (
                          <Check className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                        ) : (
                          <Lock className="w-3 h-3 text-white/20 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-white/40 text-xs mt-0.5 line-clamp-2">
                        {feature.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Free Tier Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h3 className="text-lg font-semibold mb-4 text-white/60">Included Free</h3>
          <Card className="glass-card border-white/5">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {FREE_FEATURES.map((feature) => (
                  <div key={feature} className="flex items-center gap-2 text-sm text-white/50">
                    <Check className="w-3.5 h-3.5 text-green-400/60 flex-shrink-0" />
                    {feature}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* How It Works */}
        {!isPremium && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h3 className="text-lg font-semibold mb-4">How It Works</h3>
            <div className="space-y-3">
              {[
                {
                  step: "1",
                  title: "Approve USDC",
                  description: "Your wallet approves the subscription contract to pull USDC monthly. You control the approval amount.",
                },
                {
                  step: "2",
                  title: "First Payment",
                  description: `$${price?.price_usd || 20} USDC is charged immediately. Premium features unlock instantly.`,
                },
                {
                  step: "3",
                  title: "Monthly Renewal",
                  description: "Payment is collected automatically every 30 days. 3-day grace period if your wallet is short.",
                },
                {
                  step: "4",
                  title: "Cancel Anytime",
                  description: "One click to cancel. Access continues until the end of your billing period. No penalties.",
                },
              ].map((item, i) => (
                <div key={item.step} className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#FFD700]/20 flex items-center justify-center flex-shrink-0 text-[#FFD700] font-bold text-sm">
                    {item.step}
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">{item.title}</p>
                    <p className="text-white/40 text-xs mt-0.5">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Cancel Section (for premium users) */}
        {isPremium && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="pt-8 border-t border-white/5"
          >
            <AnimatePresence mode="wait">
              {!showCancelConfirm ? (
                <motion.div
                  key="cancel-button"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-center"
                >
                  <button
                    onClick={() => setShowCancelConfirm(true)}
                    className="text-white/30 text-sm hover:text-white/60 transition-colors"
                  >
                    Cancel subscription
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  key="cancel-confirm"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  <Card className="border-red-500/20 bg-red-500/5">
                    <CardContent className="p-6 text-center space-y-4">
                      <XCircle className="w-10 h-10 text-red-400 mx-auto" />
                      <div>
                        <p className="text-white font-semibold">Cancel Premium?</p>
                        <p className="text-white/50 text-sm mt-1">
                          You'll keep access until your current period ends
                          {subscription?.days_until_expiry > 0 && ` (${subscription.days_until_expiry} days remaining)`}.
                          After that, premium features will be locked.
                        </p>
                      </div>
                      <div className="flex gap-3 justify-center">
                        <Button
                          variant="outline"
                          onClick={() => setShowCancelConfirm(false)}
                          className="border-white/20 text-white/70 hover:text-white"
                        >
                          Keep Premium
                        </Button>
                        <Button
                          onClick={handleCancel}
                          disabled={cancelling}
                          className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30"
                        >
                          {cancelling ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : null}
                          {cancelling ? "Cancelling..." : "Yes, Cancel"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Bottom CTA for non-premium */}
        {!isPremium && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="pb-8"
          >
            <Card className="glass-card border-[#FFD700]/20 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-[#FFD700]/5 via-transparent to-[#FFD700]/5 pointer-events-none" />
              <CardContent className="p-6 relative text-center space-y-3">
                <p className="text-white font-semibold">
                  The ISE fee savings alone pay for Premium
                </p>
                <p className="text-white/40 text-sm">
                  Active users save ~$24/month in swap fees with internal matching.
                  Premium costs ${price?.price_usd || 20}/month. It pays for itself.
                </p>
                <Button
                  onClick={handleSubscribe}
                  disabled={subscribing}
                  className="btn-gold px-8 py-5 rounded-xl font-semibold"
                >
                  {subscribing ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Crown className="w-5 h-5 mr-2" />
                  )}
                  Get Premium
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default Subscription;
